import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Routes, Route, Link, Navigate, useNavigate } from "react-router-dom";
import "../App.css";
import { IconContext } from "react-icons";
import logo from "../assets/images/LogoMercadito.png";
import { SidebarData, SidebarItem } from "./SidebarData";
import SubMenu from "./SubMenu";
import Login from "./Login";
import { fetchCurrentUser, forgotPasswordUser } from "../slices/auth";
import ForgotPassword from "./ForgotPassword";
import ResetPassword from "./ResetPassword";
import Users from "./Users";
import Products from "./Products";
import Categories from "./Categories";
import Variations from "./Variations";
import AdminHome from "./AdminHome";
import UnauthorizedPage from "./UnauthorizedPage";
import { State } from "../interfaces/types";
import { AppDispatch } from "../store";
import { useAppDispatch } from "../hooks";
import TermsAndConditions from "./TermsAndConditions";
import UnderConstruction from "./UnderConstruction";
import MainPage from "./client/MainPage";
import ProductDetails from "./client/ProductDetails";
import CartPage from "./client/CartPage";
import ProviderApplicationPage from "./ProviderApplicationPage";
import AdminEvents from "./AdminEvents";

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useSelector((state: State) => state.auth);

  const [sidebarVisible, setSidebarVisible] = useState<boolean>(false);
  const dispatch: AppDispatch = useAppDispatch();
  const hasRunRef = useRef(false);
  const hasFetchedCurrentUserRef = useRef(false);

  const isFirstLogin = currentUser?.firstLogin;
  const isLoggedIn = useSelector((state: any) => state.auth.isLoggedIn);

  const mustAcceptConditions =
    !!currentUser &&
    Object.prototype.hasOwnProperty.call(currentUser, "conditions") &&
    currentUser.conditions === false;
  const canShowAdminNavigation = !!currentUser && !isFirstLogin && !mustAcceptConditions;

  const toggleSidebar = () => {
    setSidebarVisible((prev) => !prev);
  };

  const CenteredRoute = ({ children }: { children: React.ReactNode }) => (
    <div className={`main centered-layout ${canShowAdminNavigation ? "has-sidebar" : ""}`}>
      {canShowAdminNavigation && (
        <IconContext.Provider value={{ color: "undefined" }}>
          <nav className="navbar navbar-expand-lg navbar-dark">
            <div className="container-fluid">
              <button
                className="navbar-toggler"
                type="button"
                onClick={toggleSidebar}
              >
                <span className="navbar-toggler-icon"></span>
              </button>

              {currentUser && (
                <Link to="/admin-home" className="navbar-brand">
                  <img
                    src={logo}
                    alt="mercadito-killa-logo"
                    className="logo-img-nav"
                  />
                </Link>
              )}

              <div className="collapse navbar-collapse">
                <ul className="navbar-nav mr-auto">
                  <li className="nav-item">
                    {currentUser && (
                      <Link to="/admin-home" className="nav-link">
                        El Mercadito de Kila
                      </Link>
                    )}
                  </li>
                </ul>
              </div>

              {currentUser && (
                <>
                  <ul className="navbar-nav ms-auto d-none d-lg-flex">
                    <li className="nav-item">
                      <Link to="/admin-home" className="nav-link">
                        Bienvenido(a),{" "}
                        {currentUser.name + " " + currentUser.lastname}
                      </Link>
                    </li>
                  </ul>

                  <div className="w-85 d-flex d-lg-none justify-content-center mt-2">
                    <span className="nav-item nav-link text-center">
                      Bienvenido(a),{" "}
                      {currentUser.name + " " + currentUser.lastname}
                    </span>
                  </div>
                </>
              )}
            </div>
          </nav>

          {currentUser && (
            <nav className={`nav-menu ${sidebarVisible ? "active" : ""}`}>
              <ul className="nav-menu-items">
                {SidebarData.map((item: SidebarItem, index: number) => (
                  <SubMenu
                    key={index}
                    item={item}
                    onClickMenuItem={toggleSidebar}
                  />
                ))}
              </ul>
            </nav>
          )}
        </IconContext.Provider>
      )}

      <div className="centered-content">{children}</div>
    </div>
  );

  useEffect(() => {
    if (!isLoggedIn) {
      hasFetchedCurrentUserRef.current = false;
      hasRunRef.current = false;
      return;
    }

    if (isLoggedIn && !currentUser && !hasFetchedCurrentUserRef.current) {
      hasFetchedCurrentUserRef.current = true;
      dispatch(fetchCurrentUser());
      return;
    }

    if (!currentUser) return;

    if (mustAcceptConditions) {
      navigate("/terms-conditions", { replace: true });
      return;
    }

    if (hasRunRef.current || !isFirstLogin || !currentUser?.username) return;

    hasRunRef.current = true;

    const { username } = currentUser;

    dispatch(forgotPasswordUser({ username }))
      .unwrap()
      .then((response) => {
        const token = response.token;
        const isNumeric = /^[0-9]+$/.test(username);

        navigate(
          isNumeric
            ? `/reset-password-client/${token}`
            : `/reset-password/${token}`,
          { replace: true }
        );
      })
      .catch((error) => {
        console.error("Error generating token:", error);
      });
  }, [currentUser, mustAcceptConditions, isFirstLogin, isLoggedIn, dispatch, navigate]);

  return (
    <Routes>
      <Route
        path="/"
        element={
          <div className="main-page-route">
            <MainPage />
          </div>
        }
      />

      <Route
        path="/productos/:id"
        element={
          <div className="main-page-route">
            <ProductDetails />
          </div>
        }
      />

      <Route
        path="/carrito"
        element={
          <div className="main-page-route">
            <CartPage />
          </div>
        }
      />

      <Route path="/quiero-ser-proveedor" element={<ProviderApplicationPage />} />

      <Route
        path="/admin-home"
        element={
          <CenteredRoute>
            <AdminHome />
          </CenteredRoute>
        }
      />

      <Route
        path="/admin"
        element={
          <CenteredRoute>
            <Login />
          </CenteredRoute>
        }
      />

      <Route
        path="/users"
        element={
          <CenteredRoute>
            <Users />
          </CenteredRoute>
        }
      />

      <Route
        path="/events"
        element={
          <CenteredRoute>
            <AdminEvents />
          </CenteredRoute>
        }
      />

      <Route
        path="/products"
        element={
          <CenteredRoute>
            <Products />
          </CenteredRoute>
        }
      />

      <Route
        path="/categories"
        element={
          <CenteredRoute>
            <Categories />
          </CenteredRoute>
        }
      />

      <Route
        path="/variations"
        element={
          <CenteredRoute>
            <Variations />
          </CenteredRoute>
        }
      />

      <Route
        path="/terms-conditions"
        element={
          <CenteredRoute>
            <TermsAndConditions />
          </CenteredRoute>
        }
      />

      <Route
        path="/forgot-password"
        element={
          <CenteredRoute>
            <ForgotPassword />
          </CenteredRoute>
        }
      />

      <Route
        path="/reset-password/:token"
        element={
          <CenteredRoute>
            <ResetPassword />
          </CenteredRoute>
        }
      />

      <Route
        path="/unauthorized-page"
        element={
          <CenteredRoute>
            <UnauthorizedPage />
          </CenteredRoute>
        }
      />

      <Route
        path="/under-construction"
        element={
          <CenteredRoute>
            <UnderConstruction />
          </CenteredRoute>
        }
      />

      <Route path="*" element={<Navigate to="/admin" replace />} />
    </Routes>
  );
};

export default Navbar;
